<script setup lang="ts">
import {computed, ref} from 'vue';
import {downloadBlob} from '@/services/download';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useNotificationStore} from '@/stores/notifications';
import {type DiatonicShiftSettings, OperationEnum, TriadReductionEnum} from '@/generated';
import Collapsible from '@/components/generic/Collapsible.vue';
import {DIATONIC_INTERVAL_OPTIONS, DIATONIC_OPERATION_OPTIONS, TRIAD_REDUCTION_OPTIONS} from './diatonicShiftOptions';

const notificationStore = useNotificationStore();
const uploadStore = useMusicUploadStore();
const {currentFileId, isAnalyzing, isShifting, shiftResponse} = storeToRefs(uploadStore);

// --- Shift settings (generierte Enums – falscher Wert fällt dem Compiler auf) ---
const operation = ref<OperationEnum>(OperationEnum.AddShiftedNotes);
const interval = ref<number>(3);
const triadReduction = ref<TriadReductionEnum>(TriadReductionEnum.LowHigh);

// --- UI state ---
const shiftStatus = ref('');
const showStatus = ref(false);

const hasShiftedResult = computed(() => !!shiftResponse.value?.music_xml);

// Stateless: das verschobene MusicXML kommt inline; Download als clientseitiger Blob.
function downloadShiftedXml() {
  const content = shiftResponse.value?.music_xml;
  if (!content) return;
  downloadBlob(new Blob([content], {type: 'application/xml'}), shiftResponse.value?.filename || 'shifted.musicxml');
}

const intervalOptions = DIATONIC_INTERVAL_OPTIONS;
const triadReductionOptions = TRIAD_REDUCTION_OPTIONS;
const operationOptions = DIATONIC_OPERATION_OPTIONS;

function handleShiftStatus(message: string) {
  shiftStatus.value = message;
  showStatus.value = !!message;
}

async function applyShift() {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }

  handleShiftStatus('Diatonische Verschiebung wird angewendet…');

  const settings: DiatonicShiftSettings = {
    enabled: true,
    operation: operation.value,
    interval: operation.value === OperationEnum.AddShiftedNotes ? interval.value : null,
    triad_reduction: triadReduction.value,
  };

  // Der Store wendet die Verschiebung an, macht das Ergebnis zur neuen Arbeitsbasis
  // (damit anschließend transponiert/konvertiert werden kann) und erfasst sie im Verlauf.
  const result = await uploadStore.applyDiatonicShift(settings);

  if (!result) {
    handleShiftStatus('');
    return;
  }

  handleShiftStatus('Verschiebung abgeschlossen.');
  notificationStore.notify('Diatonische Verschiebung angewendet.', 'success', 2000);
}

</script>

<template>
  <div class="max-w-full ">
  <!-- Status Badge (Titel liefert der umschließende Tab) -->
  <div v-if="showStatus" class="mb-4 flex justify-end">
    <div class="px-3 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100 animate-fade-in">
      {{ shiftStatus }}
    </div>
  </div>

  <div class="space-y-4">
  <!-- Operation Mode -->
  <div class="grid grid-cols-1 gap-2">
    <button
        v-for="opt in operationOptions"
        :key="opt.value"
        type="button"
        class="group flex items-center gap-2 p-2 rounded-xl border-2 transition-all duration-200 text-left"
        :class="operation === opt.value
                  ? 'bg-violet-50 border-violet-300 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-violet-200 hover:shadow-md'"
        @click="operation = opt.value"
    >
      <div
          class="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg transition-colors"
          :class="operation === opt.value
                    ? 'bg-violet-500 text-white'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-violet-100 group-hover:text-violet-600'"
      >
        <Icon :icon="opt.icon" class="text-sm"/>
      </div>
      <div>
        <div class="font-semibold text-gray-800 text-sm">{{ opt.label }}</div>
        <div class="text-xs text-gray-500">{{ opt.hint }}</div>
      </div>
    </button>
  </div>

  <!-- Interval Selector (only for add_shifted_notes) -->
  <div
      v-if="operation === OperationEnum.AddShiftedNotes"
      class="bg-gray-50/50 rounded-xl p-4 border border-gray-100"
  >
    <p class="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">Intervall</p>
    <div class="flex flex-wrap gap-2">
      <button
          v-for="opt in intervalOptions"
          :key="opt.value"
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200"
          :class="interval === opt.value
                    ? 'bg-violet-500 text-white border-violet-500 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300 hover:text-violet-700'"
          @click="interval = opt.value"
      >
        {{ opt.label }}
      </button>
    </div>
  </div>

  <!-- Triad Reduction Mode (only for reduce_triad_to_dyad) -->
  <div
      v-if="operation === OperationEnum.ReduceTriadToDyad"
      class="bg-gray-50/50 rounded-xl p-4 border border-gray-100"
  >
    <p class="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">Reduktionsmodus</p>
    <div class="flex flex-wrap gap-2">
      <button
          v-for="opt in triadReductionOptions"
          :key="opt.value"
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200"
          :class="triadReduction === opt.value
                    ? 'bg-violet-500 text-white border-violet-500 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300 hover:text-violet-700'"
          @click="triadReduction = opt.value"
      >
        {{ opt.label }}
      </button>
    </div>
  </div>
  </div>

  <!-- Action Buttons -->
  <div class="flex gap-2 mt-4">
    <button
        id="applyDiatonicShift"
        class="btn btn-primary flex-1"
        type="button"
        :disabled="isShifting || isAnalyzing"
        @click="applyShift"
    >
      <Icon v-if="isShifting" icon="fa6-solid:spinner" class="animate-spin mr-2"/>
      <Icon v-else icon="fa6-solid:layer-group" class="mr-2"/>
      {{ isShifting ? 'Verschiebe…' : 'Anwenden' }}
    </button>
  </div>

  <!-- Result Section -->
  <div
      v-if="hasShiftedResult"
      class="mt-6 p-5 bg-gradient-to-br from-violet-50 to-purple-50/30 rounded-xl border border-violet-100/50 shadow-sm animate-fade-in-up"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="font-semibold text-violet-900 flex items-center gap-2">
        <div class="p-1.5 bg-violet-100 rounded-md text-violet-600">
          <Icon icon="fa6-solid:check"/>
        </div>
        Ergebnis verfügbar
      </h3>
      <span
          v-if="shiftResponse?.filename"
          class="text-xs text-violet-700/70 truncate max-w-[150px]"
          :title="shiftResponse.filename"
      >
          {{ shiftResponse.filename }}
        </span>
    </div>

    <div class="flex flex-wrap gap-3">
      <!-- MusicXML Download (clientseitig, stateless) -->
      <button
          v-if="shiftResponse?.music_xml"
          type="button"
          @click="downloadShiftedXml"
          class="btn bg-white text-violet-700 border border-violet-200 hover:bg-violet-50 hover:border-violet-300 shadow-sm flex items-center gap-2 flex-1 justify-center"
      >
        <Icon icon="fa6-solid:file-code"/>
        <span>MusicXML herunterladen</span>
      </button>
    </div>
  </div>
  </div>
</template>
